import { useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Menu } from 'lucide-react';

import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui';
import { cn } from '@/lib/utils';
import { useAuth } from '@/pages/auth';

import { Logo } from './Logo';
import { UserMenu } from './UserMenu';
import { publicNav } from './nav-items';

interface TopNavProps {
  /**
   * - `solid` (default) — white sticky bar with dark links for normal pages.
   * - `transparent` — sits over a coloured hero (white links, no background).
   * - `brand` — solid sky-blue bar with white links for inner/static pages.
   */
  variant?: 'solid' | 'transparent' | 'brand';
}

/**
 * Public sticky top navigation. Reproduces the marketing-site chrome.
 */
export function TopNav({ variant = 'solid' }: TopNavProps) {
  const authenticated = useAuth((s) => s.status === 'authenticated');
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  // On the Register page, the mobile CTA should send people who already
  // have an account to /login instead of looping them back to /register.
  // Other pages keep the default "Join Free" → /register.
  const isRegisterPage = pathname === '/register';

  const transparent = variant === 'transparent';
  const brand = variant === 'brand';
  /** Variants that render white text/links on a coloured background. */
  const onColor = transparent || brand;

  const linkBase = cn(
    'text-sm font-medium transition-colors',
    onColor
      ? 'text-white/90 hover:text-white'
      : 'text-ink-2 hover:text-ink'
  );
  const activeClass = onColor ? 'text-white' : 'text-brand-700';

  return (
    <header
      className={cn(
        'sticky top-0 z-40 w-full transition-colors duration-200',
        transparent && 'bg-transparent',
        brand && 'bg-brand text-white shadow-sm',
        !onColor && 'border-b border-border bg-surface'
      )}
    >
      <nav className="mx-auto flex h-16 max-w-7xl items-center gap-3 px-4 sm:gap-6 sm:px-6">
        {/* Mobile hamburger — leads the bar so the brand wordmark sits next
            to it, matching the legacy mobile chrome. Hidden on md+ where
            the full desktop nav is visible. */}
        <Button
          variant="ghost"
          size="icon"
          aria-label="Open menu"
          className={cn(
            'md:hidden',
            onColor && 'text-white hover:bg-white/10 hover:text-white'
          )}
          onClick={() => setOpen(true)}
        >
          <Menu className="h-5 w-5" />
        </Button>

        <Logo />

        {/* Desktop links */}
        <ul className="hidden items-center gap-6 md:flex">
          {publicNav.map((item) => (
            <li key={item.path}>
              <NavLink
                to={item.path}
                className={({ isActive }) =>
                  cn(linkBase, isActive && activeClass)
                }
              >
                {item.label}
              </NavLink>
            </li>
          ))}
        </ul>

        <div className="ml-auto flex items-center gap-2">
          {authenticated ? (
            <UserMenu />
          ) : (
            <>
              {/* Desktop CTAs */}
              <div className="hidden items-center gap-2 md:flex">
                <Button
                  variant="ghost"
                  className={onColor ? 'text-white hover:bg-white/10 hover:text-white' : undefined}
                  onClick={() => navigate('/login')}
                >
                  Sign in
                </Button>
                <Button
                  // White-bg "Join free" across every variant (solid/brand/
                  // transparent) per request 2026-05-31; previously only the
                  // brand (sky-blue) variant inverted it. White on a coloured
                  // header pops; white on the white solid header is bordered
                  // so it still reads as a button.
                  className={cn(
                    'rounded-full bg-white text-brand-700 hover:bg-white/90',
                    variant === 'solid' && 'border border-border'
                  )}
                  onClick={() => navigate('/register')}
                >
                  Join free
                </Button>
              </div>

              {/* Mobile CTA — paired with the leading hamburger so anon
                  visitors always have a one-tap path without opening the
                  drawer. Defaults to "Join Free" → /register; on the
                  Register page itself we swap to "Sign In" → /login so
                  existing members aren't sent back to the form they're
                  already on. White pill matches the desktop treatment but
                  compacted for the mobile bar. */}
              <Button
                size="sm"
                className={cn(
                  'h-8 rounded-full bg-white px-4 text-sm font-medium text-brand-700 hover:bg-white/90 md:hidden',
                  variant === 'solid' && 'border border-border',
                )}
                onClick={() => navigate(isRegisterPage ? '/login' : '/register')}
              >
                {isRegisterPage ? 'Sign In' : 'Join Free'}
              </Button>
            </>
          )}
        </div>
      </nav>

      {/* Mobile drawer */}
      <Dialog open={open} onOpenChange={setOpen}>
        {/* The trailing arbitrary variants override the shadcn Dialog's
            default close-button styling for this drawer only: white icon at
            full opacity, bumped to 20px, no focus ring. The base Close
            `<button>` is the only direct `<button>` child of DialogContent.
            `flex flex-col` replaces shadcn's default `grid` so the header /
            nav list / sign-in CTAs stack naturally without grid implicit
            rows leaving an empty band under the brand strip.
            `onOpenAutoFocus` cancels Radix's "focus the first focusable on
            open" behavior so the close button doesn't start the drawer
            already-focused. */}
        <DialogContent
          onOpenAutoFocus={(e) => e.preventDefault()}
          className="right-auto left-0 top-0 flex h-full max-w-xs translate-x-0 translate-y-0 flex-col gap-0 rounded-none border-0 p-0 data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:rounded-none [&>button]:top-5 [&>button]:text-white [&>button]:opacity-100 [&>button]:ring-0 [&>button]:focus:ring-0 [&>button]:focus:ring-offset-0 [&>button>svg]:h-5 [&>button>svg]:w-5"
        >
          {/* Sky-blue brand strip — 62px tall to match the design spec. */}
          <DialogHeader className="h-[62px] justify-center bg-brand px-5 text-left">
            <DialogTitle>
              <Logo />
            </DialogTitle>
          </DialogHeader>
          <ul className="flex flex-col p-2">
            {publicNav.map((item) => {
              const Icon = item.icon;
              return (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-ink-2 transition-colors hover:bg-accent',
                        isActive && 'bg-accent text-brand-700'
                      )
                    }
                  >
                    <Icon className="h-4 w-4" />
                    {item.label}
                  </NavLink>
                </li>
              );
            })}
          </ul>
          {!authenticated ? (
            <div className="mt-auto flex flex-col gap-2 border-t border-border p-4">
              <Button
                variant="outline"
                asChild
                onClick={() => setOpen(false)}
              >
                <Link to="/login">Sign in</Link>
              </Button>
              <Button
                className="rounded-full"
                asChild
                onClick={() => setOpen(false)}
              >
                <Link to="/register">Join free</Link>
              </Button>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </header>
  );
}
